// pages/UserManagementPage.js
import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import UserService from '../services/UserService'
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../firebase'
import './UserManagementPage.css'

function UserManagementPage () {
  const { user } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingUser, setEditingUser] = useState(null)
  const [companyId, setCompanyId] = useState(null)

  const db = getFirestore(app)
  const functions = getFunctions(app, 'us-central1')

  useEffect(() => {
    if (!user) {
      setLoading(false)
      setError('Bitte melden Sie sich an')
      return
    }

    let unsubscribe = () => {}

    const setupListener = async () => {
      try {
        // Get user's companyId
        const userCompanyId = await UserService.getUserCompanyId(user.uid)
        setCompanyId(userCompanyId)

        // Real-time listener for company-specific users
        const usersRef = collection(db, 'companies', userCompanyId, 'users')
        const q = query(usersRef, orderBy('createdAt', 'desc'))

        unsubscribe = onSnapshot(
          q,
          snapshot => {
            const companyUsers = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            setUsers(companyUsers)
            setLoading(false)
          },
          err => {
            console.error('Error loading users:', err)
            setError('Fehler beim Laden der Benutzer')
            setLoading(false)
          }
        )
      } catch (err) {
        console.error('Error setting up listener:', err)
        setError('Fehler beim Initialisieren')
        setLoading(false)
      }
    }

    setupListener()

    return () => unsubscribe()
  }, [db, user])

  const handleUpdateRole = async (userId, newRole) => {
    try {
      if (!companyId) {
        setError('Company ID nicht gefunden')
        return
      }

      const targetUser = users.find(u => u.id === userId)
      const currentUserRole = user?.userData?.role
      const targetUserRole = targetUser?.role || 'user'

      if (currentUserRole === 'admin' && targetUserRole === 'superadmin') {
        setError(
          'Administratoren dürfen Super-Administratoren nicht bearbeiten.'
        )
        setEditingUser(null) // Bearbeitungsmodus beenden
        return
      }

      // Call backend function to update both user documents
      const updateUserRole = httpsCallable(functions, 'updateUserRole')
      const result = await updateUserRole({
        targetUserId: userId,
        newRole: newRole,
        companyId: companyId
      })

      console.log('User role updated:', result.data)
      setEditingUser(null)
      setError(null)
    } catch (error) {
      console.error('Error updating user role:', error)
      setError(error.message || 'Fehler beim Aktualisieren der Benutzerrolle')
    }
  }

  const handleDeleteUser = async userId => {
    if (
      !window.confirm(
        'Sind Sie sicher, dass Sie diesen Benutzer löschen möchten? Dies kann nicht rückgängig gemacht werden. Der Benutzer wird vollständig gelöscht, einschließlich des Firebase Auth-Kontos.'
      )
    ) {
      return
    }

    try {
      if (!companyId) {
        setError('Company ID nicht gefunden')
        return
      }

      const targetUser = users.find(u => u.id === userId)
      const currentUserRole = user?.userData?.role
      const targetUserRole = targetUser?.role || 'user'

      if (currentUserRole === 'admin' && targetUserRole === 'superadmin') {
        setError(
          'Administratoren dürfen Super-Administratoren nicht löschen.'
        )
        return
      }

      // Call backend function to delete user completely
      const deleteUser = httpsCallable(functions, 'deleteUser')
      const result = await deleteUser({
        targetUserId: userId,
        companyId: companyId
      })

      console.log('User deleted:', result.data)
      setError(null)
    } catch (error) {
      console.error('Error deleting user:', error)
      setError(
        error.message || 'Fehler beim Löschen des Benutzers'
      )
    }
  }

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getRoleBadgeClass = role => {
    switch (role) {
      case 'superadmin':
        return 'role-superadmin'
      case 'admin':
        return 'role-admin'
      case 'moderator':
        return 'role-moderator'
      default:
        return 'role-user'
    }
  }

  const getRoleLabel = role => {
    switch (role) {
      case 'superadmin':
        return 'Super-Administrator'
      case 'admin':
        return 'Administrator'
      case 'moderator':
        return 'Moderator'
      default:
        return 'Benutzer'
    }
  }

  if (loading) {
    return (
      <div className='user-management'>
        <div className='loading'>Lädt Benutzer...</div>
      </div>
    )
  }

  return (
    <div className='user-management'>
      <div className='page-header'>
        <div>
          <h1>Benutzerverwaltung</h1>
          <p className='page-subtitle'>
            {users.length} Benutzer in Ihrer Organisation
          </p>
        </div>
      </div>

      {error && (
        <div className='error-message'>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {users.length === 0 ? (
        <div className='empty-state'>
          <p>Keine Benutzer gefunden.</p>
        </div>
      ) : (
        <div className='users-table-container'>
          <table className='users-table'>
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Rolle</th>
                <th>Erstellt am</th>
                <th>Letzter Login</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map(userItem => {
                const currentUserRole = user?.userData?.role
                const targetUserRole = userItem.role || 'user'

                const canEdit =
                  userItem.id !== user.uid && // Man kann sich nicht selbst bearbeiten
                  (currentUserRole === 'superadmin' || // Super-Admins dürfen alle bearbeiten
                    (currentUserRole === 'admin' && // Admins dürfen...
                      targetUserRole !== 'superadmin')) // ...alle bearbeiten, AUSSER Super-Admins
                return (
                  <tr key={userItem.id}>
                    <td>
                      <div className='user-name'>
                        <div className='user-avatar'>
                          {(
                            userItem.name?.[0] ||
                            userItem.email?.[0] ||
                            'U'
                          ).toUpperCase()}
                        </div>
                        <span>{userItem.name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className='user-email'>{userItem.email}</td>
                    <td>
                      {editingUser === userItem.id ? (
                        <div className='role-edit'>
                          <select
                            value={userItem.role || 'user'}
                            onChange={e =>
                              handleUpdateRole(userItem.id, e.target.value)
                            }
                            className='role-select'
                          >
                            <option value='user'>Benutzer</option>
                            <option value='moderator'>Moderator</option>
                            <option value='admin'>Administrator</option>
                            {user?.userData?.role === 'superadmin' && (
                              <option value='superadmin'>
                                Super-Administrator
                              </option>
                            )}
                          </select>
                          <button
                            className='btn-cancel'
                            onClick={() => setEditingUser(null)}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className='role-display'>
                          <span
                            className={`role-badge ${getRoleBadgeClass(
                              userItem.role
                            )}`}
                          >
                            {getRoleLabel(userItem.role || 'user')}
                          </span>
                          {canEdit && (
                            <button
                              className='btn-edit-role'
                              onClick={() => setEditingUser(userItem.id)}
                              title='Rolle bearbeiten'
                            >
                              ✎
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className='date-cell'>
                      {formatDate(userItem.createdAt)}
                    </td>
                    <td className='date-cell'>
                      {formatDate(userItem.lastLoginAt)}
                    </td>
                    <td>
                      <div className='action-buttons'>
                        {canEdit && (
                          <button
                            className='btn-delete'
                            onClick={() => handleDeleteUser(userItem.id)}
                            title='Benutzer löschen'
                          >
                            Löschen
                          </button>
                        )}
                        {userItem.id === user.uid && (
                          <span className='self-indicator'>Sie selbst</span>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className='user-stats'>
        <div className='stat-card'>
          <div className='stat-value'>
            {users.filter(u => u.role === 'superadmin').length}
          </div>
          <div className='stat-label'>Super-Administratoren</div>
        </div>
        <div className='stat-card'>
          <div className='stat-value'>
            {users.filter(u => u.role === 'admin').length}
          </div>
          <div className='stat-label'>Administratoren</div>
        </div>
        <div className='stat-card'>
          <div className='stat-value'>
            {users.filter(u => u.role === 'moderator').length}
          </div>
          <div className='stat-label'>Moderatoren</div>
        </div>
        <div className='stat-card'>
          <div className='stat-value'>
            {users.filter(u => !u.role || u.role === 'user').length}
          </div>
          <div className='stat-label'>Benutzer</div>
        </div>
      </div>
    </div>
  )
}

export default UserManagementPage
